import type { Translations } from '../types';

export const fr: Translations = {
    identityVerification: {
        title: 'Confirmez votre identité',
        mockNotice:
            'Mode test. Evrotrust n’est pas connecté. Aucun document d’identité ni donnée biométrique n’est collecté.',
        consent: 'Je comprends qu’il s’agit d’un test et j’accepte l’enregistrement d’un résultat de test.',
        continue: 'Continuer la vérification',
        help: 'Pour obtenir de l’aide, contactez le représentant de votre société.',
        expires: 'Invitation valable jusqu’au {date}',
        invalidTitle: 'Invitation indisponible',
        invalidDescription:
            'Le lien a expiré, a été révoqué ou n’est plus valide. Contactez le représentant de votre société.',
        error: 'La demande a échoué. Réessayez.',
        retry: 'Réessayer',
        loading: 'Chargement de l’invitation…',
        successTitle: 'Vérification terminée',
        successDescription: 'Votre résultat de test a été enregistré. Vous pouvez fermer cette page.',
        roles: {
            admin: 'Directeur',
            beneficiary: 'Bénéficiaire',
            beneficiary_admin: 'Directeur et bénéficiaire',
        },
    },
    kyc: {
        title: 'Vérification des documents',
        openSection: 'Ouvrir la vérification des documents',
        progress: 'État de la vérification',
        progressCount: '{completed} étapes obligatoires terminées sur {total}',
        refresh: 'Actualiser',
        hint: 'Ajoutez les documents manquants. Les statuts sont actualisés selon les résultats de la vérification.',
        loading: 'Chargement de la vérification…',
        retry: 'Réessayer',
        empty: 'Aucune étape disponible pour le moment. Réessayez plus tard ou contactez le support.',
        documents: 'Documents de la société',
        identity: 'Vérification d’identité',
        otherChecks: 'Vérifications complémentaires',
        rejectedHint: 'La vérification a échoué. Contactez le support si nécessaire.',
        openDocument: 'Ouvrir le document',
        upload: 'Ajouter un fichier',
        uploadAgain: 'Ajouter à nouveau',
        uploading: 'Envoi en cours…',
        uploadSaved: 'Le document a été enregistré et transmis pour vérification.',
        continue: 'Continuer',
        successTitle: 'Vérification de la société terminée',
        successDescription: 'Toutes les étapes obligatoires de vérification ont été validées.',
        done: 'Terminé',
        statuses: {
            completed: 'Vérifié',
            failed: 'Vérification échouée',
            in_progress_auto: 'Vérification automatique',
            in_progress_manual: 'En cours d’examen',
            upload: 'Document à ajouter',
            waiting: 'En attente de vérification',
            notRequired: 'Non requis',
        },
        errors: {
            emptyFile: 'Le fichier sélectionné est vide.',
            largeFile: 'Le fichier ne doit pas dépasser 10 Mio.',
            invalidFile: 'Choisissez un PDF ou une image JPEG, PNG, GIF, BMP, WebP.',
            conflict: 'L’étape a changé. Les données sont actualisées ; vérifiez les actions disponibles.',
            upload: 'L’envoi n’a pas pu être confirmé. Consultez la liste actualisée avant de réessayer.',
            load: 'Impossible d’actualiser la vérification. Réessayez.',
        },
        stages: {
            kbo: {
                title: 'Extrait KBO',
                description: 'Informations de la société provenant du registre officiel.',
            },
            company_charter: {
                title: 'Statuts de la société',
                description: 'Statuts actuels, y compris les modifications.',
            },
            ubo: {
                title: 'Registre UBO',
                description: 'Informations sur les bénéficiaires effectifs de la société.',
            },
            annual_report: {
                title: 'Rapport annuel',
                description: 'États financiers du dernier exercice clôturé.',
            },
            admin_id: {
                title: 'Identité des dirigeants',
                description: 'État global de vérification des dirigeants actuels.',
            },
            beneficiaries: {
                title: 'Identité des bénéficiaires effectifs',
                description: 'État global de vérification des bénéficiaires effectifs.',
            },
            pep_individual: {
                title: 'Vérifications complémentaires des dirigeants',
                description: 'Cette vérification est effectuée par notre équipe.',
            },
            pep_entity: {
                title: 'Vérifications complémentaires de la société',
                description: 'Cette vérification est effectuée par notre équipe.',
            },
            risk_report: {
                title: 'Examen final',
                description: 'Cette vérification est effectuée par notre équipe.',
            },
            bank_account: {
                title: 'Coordonnées bancaires',
                description: 'État global de vérification des coordonnées bancaires.',
            },
            other: {
                title: 'Vérification complémentaire',
                description: 'État de vérification de la société.',
            },
        },
    },
    common: {
        close: 'Fermer',
        ok: 'OK',
    },
    sidebar: {
        appTitle: 'Office Hof',
        menu: {
            mail: 'Courrier',
            office: 'Bureau',
            payment: 'Paiement',
            support: 'Support',
            profile: 'Profil',
            logout: 'Déconnexion',
        },
    },
    login: {
        title: 'Connectez-vous à votre compte',
        subtitle: 'Compte personnel',
        loadingData: 'Chargement de vos données',
        form: {
            emailLabel: 'Identifiant',
            emailPlaceholder: 'Adresse e-mail',
            passwordLabel: 'Mot de passe',
            passwordPlaceholder: 'Entrez le mot de passe',
            rememberMe: 'Se souvenir de moi',
            rememberMeTooltip: 'Enregistrer les données de connexion pendant 3 mois',
            submitButton: 'Se connecter',
            submitButtonLoading: 'Connexion...',
            forgotPassword: 'Mot de passe oublié ?',
            noAccountText: 'Pas de compte ?',
            registerLink: "S'inscrire",
            forgotPasswordEmailSubject: 'Veuillez changer mon mot de passe',
            forgotPasswordEmailBody:
                'Veuillez changer le mot de passe de mon compte :\nemail : {email},\nentreprise : {company}.',
            forgotPasswordEmailRequiredTitle: 'Saisissez l’e-mail',
            forgotPasswordEmailRequiredMessage:
                'Veuillez d’abord saisir l’e-mail de votre compte dans le champ ci-dessus, puis cliquer sur « Mot de passe oublié? ».',
            selectCompany: 'Entreprise',
            passwordDisabledHint: 'Entrez une adresse e-mail valide',
            recoveryTitle: 'Récupération',
            loginLabel: 'Identifiant',
            companyLabel: 'Entreprise',
            newPasswordLabel: 'Votre nouveau mot de passe',
            copyButton: 'Copier',
            savePasswordWarning: 'Mémorisez le nouveau mot de passe !',
            recoveryFailedMessage: "Échec de la récupération de l'accès, veuillez réessayer",
            backToLogin: 'Retour à la connexion',
            confirmRecoveryMessage: 'Confirmez la récupération du compte',
            confirmRecoveryButton: 'Confirmer',
        },
        validation: {
            emailRequired: 'Entrez une adresse e-mail',
            emailInvalid: 'Entrez une adresse e-mail valide',
            passwordRequired: 'Entrez le mot de passe',
            incorrectCredentials: 'Identifiant ou mot de passe incorrect',
            userNotFound: 'Utilisateur non trouvé',
        },
        errors: {
            authError: "Erreur d'authentification",
            invalidCredentials: 'Identifiant ou mot de passe invalide',
            tooManyAttempts: 'Trop de tentatives, réessayez plus tard',
            logoutError: 'Échec de la déconnexion',
        },
    },
    registration: {
        title: 'Inscription',
        emailLabel: 'Adresse e-mail',
        emailPlaceholder: 'Entrez votre e-mail',
        continueButton: 'Continuer',
        haveAccount: 'Vous avez déjà un compte ?',
        signInLink: 'Connexion',
        confirmTitle: "Confirmation de l'adresse e-mail",
        confirmMessage: 'Entrez le code envoyé à {email}',
        confirmBack: 'Retour',
        changeEmail: 'Utiliser un autre e-mail',
        confirmSpamHint: 'Pas de code ? Vérifiez les courriers indésirables.',
        confirmRequestAgainIn: 'Vous pourrez demander un nouveau code dans : {time}',
        confirmRequestAgainButton: 'Demander à nouveau',
        validation: {
            emailRequired: "Entrez l'e-mail",
            emailInvalid: 'Entrez un e-mail valide',
        },
        errors: {
            invalidEmail: 'E-mail invalide',
            rateLimit: 'Trop de tentatives. Réessayez plus tard.',
            serverError: 'Erreur serveur, réessayez plus tard.',
            wrongCode: 'Code incorrect',
        },
        form: {
            stepSelectTariff: 'Choisissez une formule',
            stepCompanyDetails: 'Renseignez les données de votre entreprise',
            stepAddressDetails: 'Renseignez votre adresse postale',
            addressForwardingHint: 'Le courrier papier et les colis seront redirigés vers cette adresse',
            stepPersonalDetails: 'Renseignez vos données personnelles',
            stepComplete: 'Terminé !',
            next: 'Suivant',
            back: 'Retour',
            finish: 'Terminer',
            stepSelectTariffStub: 'Le choix de la formule s’affichera ici.',
            stepCompanyDetailsStub: 'Les champs sur votre entreprise s’afficheront ici.',
            stepPersonalDetailsStub: 'Les champs sur vos données personnelles s’afficheront ici.',
            stepCompleteStub: 'Inscription terminée. Appuyez sur Terminer pour vous connecter.',
            loadingRegCustomerData: 'Chargement des données',
            selectOfficeLabel: 'Choisissez un bureau',
            selectTariffLabel: 'Choisissez une formule',
            officeBrussels: 'Bruxelles',
            officeFlanders: 'Flandre',
            companyNameLabel: 'Nom de l’entreprise',
            companyNamePlaceholder: 'Entrez le nom de l’entreprise',
            activityLabel: 'Activité',
            activityPlaceholder: 'Décrivez l’activité de l’entreprise',
            innLabel: 'Numéro TVA',
            innPlaceholder: 'Entrez le numéro TVA',
            promoCodeLabel: 'Code promo',
            promoCodePlaceholder: 'Entrez le code promo',
            ifAvailable: 'si disponible',
            tariffDetailsLink: 'En savoir plus sur les tarifs',
            registrationFor: 'Inscription pour',
            firstNameLabel: 'Prénom',
            firstNamePlaceholder: 'Entrez le prénom',
            lastNameLabel: 'Nom',
            lastNamePlaceholder: 'Entrez le nom',
            countryLabel: 'Pays',
            countryPlaceholder: 'Choisissez un pays',
            cityLabel: 'Ville',
            cityPlaceholder: 'Entrez la ville',
            lineLabel: 'Rue et numéro',
            linePlaceholder: 'Entrez la rue et le numéro',
            postcodeLabel: 'Code postal',
            postcodePlaceholder: 'Entrez le code postal',
            countryBelgium: 'Belgique',
            countryFrance: 'France',
            countryGermany: 'Allemagne',
            countryNetherlands: 'Pays-Bas',
            countryPoland: 'Pologne',
            genderLabel: 'Genre',
            genderMale: 'Homme',
            genderFemale: 'Femme',
            personalAddressLabel: 'Adresse',
            personalAddressPlaceholder: 'Entrez l’adresse',
            phoneLabel: 'Téléphone',
            phonePlaceholder: 'Entrez le téléphone',
            passportFileLabel: 'Scan du passeport',
            uploadPassportFrontButton: 'Télécharger le recto',
            uploadPassportReverseButton: 'Télécharger le verso',
            uploadAnotherPassportFileButton: 'Choisir un autre fichier',
            passportReverseFileLabel: 'Verso du passeport',
            passportFrontSideLabel: 'Recto du passeport',
            openUploadedFile: 'Ouvrir le fichier',
            completeRegistrationTitle: 'Confirmez l’inscription',
            completeRegistrationMessage:
                'Après confirmation, le formulaire rempli sera envoyé à la modération.',
            completeRegistrationConfirmButton: 'Confirmer',
            completeRegistrationCancelButton: 'Annuler',
            completeValidationFieldsTitle: 'Vérifiez les champs suivants :',
            completeValidationFieldError: 'Vérifiez ce champ',
            completeValidationInnHint: 'Vérifiez le champ TVA/INN à l’étape des informations de l’entreprise',
            moderationInProgressTitle: 'Vos données sont en cours de modération',
            moderationInProgressDescription:
                'La vérification prend jusqu’à 24 heures. Une fois terminée, nous enverrons un e-mail à {email} avec votre identifiant et votre mot de passe pour accéder à votre espace personnel.',
            moderationInProgressContact: 'Si vous avez des questions, contactez-nous à :',
            returnToWebsiteButton: 'Retourner au site',
            registerButton: 'S’inscrire',
            requiredField: 'Champ obligatoire',
            minLengthTemplate: 'Minimum {n} caractères',
            maxLengthTemplate: 'Maximum {n} caractères',
            phoneInvalid: 'Le téléphone peut contenir uniquement des chiffres, + et des parenthèses',
            latinOnly: 'Utilisez uniquement des caractères latins',
        },
    },
    pages: {
        mail: {
            title: 'Courrier',
            forwardToHomeAddress: 'Configurer le transfert du courrier vers mon adresse personnelle',
            forwardToHomeAddressDescription:
                "Toutes les lettres seront automatiquement transférées à l'adresse indiquée",
            configureForwarding: 'Configurer le transfert',
            incomingMailSettings: 'Paramètres de numérisation du courrier',
            openQrCode: 'Clé QR code pour Office Hof',
            orderForwarding: 'Commander le transfert du courrier',
            forwarding: 'Réexpédition du courrier papier',
            orderForwardingTitle: 'Commander le transfert du courrier',
            orderOneTimeForwarding: 'Commander un transfert unique',
            orderForwardingMessage:
                'Commandez le transfert du courrier et nous enverrons toutes vos lettres du Office Hof à votre adresse',
            orderButton: 'Commander',
            orderForwardingSuccess: 'Transfert commandé',
            orderForwardingError: 'Erreur lors de la commande du transfert',
            forwardingSentMessage: 'Les lettres ont été transférées à votre adresse. Confirmez la réception.',
            forwardingSentHint: "Si les lettres n'arrivent pas sous 10 jours, contactez le support",
            forwardingTrackNumber: 'Numéro de suivi',
            forwardingTrackOnBpost: 'Suivre sur bpost',
            forwardingReceivedButton: 'Transfert reçu',
            forwardingReceivedSuccess: 'Réception du transfert confirmée',
            forwardingReceivedError: 'Erreur lors de la confirmation de réception',
            forwardingEmptyMessage:
                'Transfert annulé car la boîte aux lettres était vide au moment de la demande',
            singleForwardingStatus: {
                processing: 'En cours',
                completed: 'Terminé',
                pending: 'En attente',
                cancelled: 'Annulé',
                sent: 'Envoyé',
            },
            singleForwardingRequestDate: 'Date de la demande',
            singleForwardingStatusLabel: 'Statut',
            cancelButton: 'Annuler',
            qrCodeTitle: 'QR Code personnel',
            qrCodeDescription:
                'Avec ce QR code vous pouvez accéder au bureau, aux salles de réunion réservées et à votre boîte aux lettres',
            qrCodeDescriptionWithAddress:
                "Avec ce QR code vous pouvez accéder au bureau à l'adresse {address}, aux salles de réunion réservées et à votre boîte aux lettres",
            scanContent: 'Numériser le contenu du courrier',
            scanContentDescription: 'Numériser automatiquement le contenu du courrier entrant',
            scanningSettings: 'Paramètres de numérisation',
            scanningSettingsPaperStorageTooltip:
                "Les originaux papier des lettres seront conservés dans votre boîte aux lettres personnelle, située à l'intérieur du centre d'affaires.",
            scanningDescription:
                'Si, pour des raisons de confidentialité, vous ne souhaitez pas que votre courrier papier soit ouvert et numérisé par notre personnel, veuillez désactiver la fonctionnalité ci-dessous. Les notifications de réception du courrier resteront gratuites.',
            forwardingSettings: 'Paramètres de transfert',
            regularForwarding: 'Transfert régulier',
            oneTimeForwarding: 'Transfert unique',
            forwardMail: 'Transférer le courrier',
            forwardNever: 'Jamais',
            forwardOncePerMonth: 'Une fois par mois',
            forwardTwicePerMonth: 'Deux fois par mois',
            forwardingDescription: 'Le transfert du courrier entrant sera vers votre adresse : {address}',
            changeAddressHint: "Pour changer l'adresse, écrivez au support",
            changeAddressButton: "Changer l'adresse",
            forwardingInfo: 'Vous pouvez configurer un transfert régulier ou commander un transfert unique',
            sendRegisteredMail: 'Envoyer en courrier recommandé',
            settingsSaved: 'Paramètres enregistrés',
            saveError: "Erreur lors de l'enregistrement des paramètres",
            mailsList: 'Liste du courrier',
            noMails: 'Aucun courrier',
            loading: 'Chargement...',
            loadError: 'Erreur de chargement du courrier',
            unavailable: 'Indisponible',
            mailTypeLetter: 'Lettre',
            mailTypeLetterNoScan: 'Lettre (non scannée)',
            mailTypeParcel: 'Colis',
            parcelTypeSmall: 'Petit',
            letterNoScanModalTitle: 'Lettre non scannée',
            letterNoScanModalMessage:
                'Votre courrier papier est stocké chez Office Hof. Vous pouvez le récupérer vous-même ou commander une réexpédition.',
            letterNoScanOrderButton: 'Commander la réexpédition',
            parcelTypeBig: 'Grand',
            viewButton: 'Voir',
            downloadButton: 'Télécharger',
            paymentHintButton: 'Indication',
            paymentRequiredTitle: 'Paiement requis',
            paymentRequiredMessage: 'Vous devez payer une nouvelle période pour consulter ce courrier',
            payButton: 'Payer',
            searchByDate: 'Rechercher par date',
            thisWeek: 'Cette semaine',
            thisMonth: 'Ce mois-ci',
            parcelReceivedTitle: 'Vous avez reçu un colis {type}',
            parcelReceivedBig: 'grand',
            parcelReceivedSmall: 'petit',
            parcelPickupAddress: "Vous pouvez le récupérer au bureau à l'adresse",
            okButton: 'OK',
            dateFrom: 'Du',
            dateTo: 'Au',
            resetButton: 'Réinitialiser',
            applyButton: 'Appliquer',
            dateFromAfterTo: 'La date « Du » ne peut pas être postérieure à « Au »',
            dateToBeforeFrom: 'La date « Au » ne peut pas être antérieure à « Du »',
            clearFilter: 'Effacer le filtre',
        },
        office: {
            title: 'Centre de bureaux',
            inDevelopment: 'En cours de développement',
            meetingRooms: 'Salles de réunion',
            meetingRoomsDescription: 'Réserver et gérer les salles de réunion',
            coworking: 'Coworking',
            coworkingDescription: 'Réserver un espace de coworking',
            noCoworkingAvailable: 'Aucun espace de coworking disponible',
            capacity: 'Capacité',
            people: 'personnes',
            fromPrice: 'À partir de',
            perHour: 'par heure',
            includingVat: 'TVA incl.',
            exclVat: 'HTVA',
            book: 'Réserver',
            location: 'Emplacement',
            selectLocation: "Sélectionner l'emplacement",
            allLocations: 'Tous les emplacements',
            noRoomsAvailable: 'Aucune salle disponible',
            rooms: {
                charlemagne: {
                    name: '"Charlemagne" (Vert)',
                    description:
                        'Équipement : Vidéoprojecteur, tableau blanc et marqueurs, système audio, téléphone de conférence. Services supplémentaires : Café, thé et eau offerts gratuitement pendant la réservation.',
                },
                ambiorix: {
                    name: '"Ambiorix" (Rouge)',
                    description:
                        "Équipée d'un vidéoprojecteur LED et écran, tableau blanc et marqueurs, système audio, téléphone de conférence. Services supplémentaires : Café, thé et eau offerts gratuitement pendant la réservation.",
                },
            },
            myBookings: 'Mes réservations',
            noBookings: "Vous n'avez pas encore de réservations",
            bookingQrModalTitle: 'Accès à la salle de réunion',
            bookingQrModalMessage: 'Pour accéder à la salle de réunion, présentez le QR code',
            showQrCode: 'Afficher le QR code',
            hours: 'h',
            minutes: 'min',
            datePlaceholder: 'jj.mm.aaaa',
            duration: 'Durée',
            cost: 'Coût',
            insufficientFundsTitle: 'Fonds insuffisants',
            insufficientFundsMessage: "Vous n'avez pas assez de fonds",
            insufficientFundsMessageWithAmount:
                "Vous n'avez pas assez de fonds pour réserver. Il vous manque €{amount} sur votre solde",
            showMore: 'Plus',
            hide: 'Masquer',
            booking: 'Réservation',
            selectDate: 'Sélectionner la date',
            noSlotsAvailable: 'Aucun créneau disponible',
            roomNotSelected: 'Salle de réunion non sélectionnée',
            available: 'Disponible',
            booked: 'Réservé',
            bookingSuccess: 'Salle de réunion réservée avec succès',
            bookingError: 'Erreur lors de la création de la réservation',
            startTime: 'Début',
            endTime: 'Fin',
            selectStartTime: "Sélectionner l'heure",
            selectEndTime: "Sélectionner l'heure",
        },
        payment: {
            title: 'Paiement',
            showContract: 'Contrat avec Office Hof',
            balance: 'Solde',
            extraChargesThisMonth: 'Services supplémentaires facturés ce mois-ci',
            extraChargesSectionTitle: 'Facturations ce mois-ci pour services supplémentaires',
            extraServiceTypes: {
                scan: 'Numérisation du courrier',
                parcels: 'Envoi de lettres/colis',
                bookings: 'Réservations de salles de réunion',
                tariff: 'Tarif',
                postal_fees: 'Frais postaux',
            },
            extraChargesTotalLabel: 'Total',
            tariff: 'Tarif',
            nextChargeDate: 'Prochain prélèvement : ',
            tariffNotActivated: 'Le forfait est activé après le paiement',
            replenishBalance: 'Recharger le solde',
            replenish: 'Recharger',
            pay: 'Payer',
            paySubscriptionTitle: "Payez l'abonnement",
            paySubscriptionMessage: "Cette page sera accessible après le paiement de l'abonnement",
            availableAfterPrepayment: 'Disponible après prépaiement',
            enterAmount: 'Entrez le montant et sélectionnez le type de paiement',
            paymentCredited:
                "Les paiements sont généralement crédités immédiatement, mais peuvent prendre jusqu'à 2 jours dans certains cas",
            paymentScreenTitle: 'Paiement',
            enterAmountForPayment: 'Entrez le montant à payer',
            amountForPayment: 'Montant à payer',
            replenishBalanceOn: 'Recharger le solde de :',
            totalToPay: 'Total à payer :',
            payByRequisites: 'Payer par virement',
            requisiteCompany: 'Entreprise',
            payOnline: 'Payer en ligne',
            payFor1Month: "Payer 1 mois à l'avance",
            payFor3Months: "Payer 3 mois à l'avance",
            payFor6Months: "Payer 6 mois à l'avance",
            payFor12Months: "Payer 1 an à l'avance",
            discount: 'Réduction',
            discountOnTariff: 'Réduction de {discount}% sur le tarif pour {months} mois !',
            discountOnTariffTooltip:
                "En payant €{amount}, vous bénéficiez d'une réduction de {discount}% sur le tarif pendant {months} mois !",
            customAmount: 'Montant personnalisé',
            minAmountError: 'Montant minimum de paiement : {amount}€',
            serverError: 'Erreur serveur, nous nous en occupons',
            chargesHistory: 'Historique des prélèvements',
            invoice: 'Facture du',
            noInvoices: 'Aucune facture trouvée',
            noTariff: "Vous n'avez pas de forfait",
            currentTariff: 'Actuel',
            sameAsPreviousTariff: 'Tout de {tariffName}, et en plus :',
            fromPrice: 'À partir de',
            perMonth: 'par mois',
            withoutVat: 'hors TVA 21 %',
            goToTariff: 'Changer de forfait',
            tariffUpgradeSubject: 'Demande du client {customer_id} de passer au tarif {tariff_name}',
            tariffUpgradeBody: 'Veuillez faire passer mon tarif à {tariff_name}',
            tariffFeatures: {
                legalAddress: "L'adresse légale de votre entreprise",
                receptionMails: 'Réception de votre courrier entrant (recommandé incl.) et colis',
                coffeeSoftDrinks:
                    'Café et boissons non alcoolisées pour vous et vos clients en salle de réunion',
                access24_7: 'Accès à vos informations et messages 24h/24 et 7j/7',
                sharedFaxLine: 'Ligne fax entrante partagée (traitée comme courrier standard)',
                mailScanning:
                    'Numérisation de votre courrier entrant et redirection vers votre boîte mail au quotidien',
                freePages:
                    "Nombre de feuilles gratuites (numérisation et redirection A4 recto-verso) incluses dans l'abonnement mensuel",
                personalBox: 'Boîte personnelle pour documents, accès 24h/24',
                ipTelephony: "Service de téléphonie IP avec poste dans l'IP PBX Office Hof",
                freeMeetingHours: 'Heures de réservation gratuites des salles de réunion par mois',
                freeParcels: "Nombre de colis entrants gratuits inclus dans l'abonnement mensuel",
                discountLevel:
                    'Niveau de réduction sur réservation de salles et postes, numérisation et traitement des colis',
                brusselsPhoneLine:
                    "Ligne directe Bruxelles (numéros d'autres régions et pays disponibles) via IP PBX",
                phoneCallsManagement: "Gestion des appels et fax entrants et sortants avec l'IP PBX dédiée",
            },
        },
        support: {
            title: 'Contacter le support',
            phone: 'Téléphone',
            email: 'E-mail',
            phoneHint: '9h–18h, jours ouvrables',
            writeButton: 'Écrire au support',
            emailSubject: 'Demande du client {customer_id}',
            editDataEmailSubject: 'Demande de modification des données du client {customer_id}',
            innEmailSubject: 'Information du client {customer_id}',
            innEmailBody: 'Bonjour !\n\nMon numéro de TVA : \n',
            innModalTitle: 'Numéro de TVA requis',
            innModalMessage:
                'Veuillez indiquer votre numéro de TVA — nous le transmettrons pour vérification.',
            innModalSubmitButton: 'Envoyer',
            innModalLaterButton: 'Plus tard',
            innModalCloseButton: 'Fermer',
            innRequiredModalTitle: 'Numéro fiscal requis',
            innRequiredModalMessage:
                'Le délai de {n} jours pour communiquer votre numéro fiscal est écoulé. Indiquez-le ci-dessous pour continuer à utiliser le service.',
            innInputLabel: 'Numéro de TVA',
            innInputPlaceholder: 'Saisissez votre numéro de TVA',
            innInputRequired: 'Veuillez saisir votre numéro de TVA',
            innResubmitButton: 'Renvoyer',
            innPendingTitle: 'Numéro de TVA en cours de vérification',
            innPendingMessage:
                "Nous avons reçu votre numéro de TVA {tax_id} et l'avons transmis pour vérification. L'accès au service reste limité jusqu'à la fin de la vérification.",
            innPendingResubmitHint:
                "La vérification prend plus de temps que d'habitude. Vous pouvez renvoyer le numéro de TVA.",
            innSubmitError: "Impossible d'envoyer le numéro de TVA. Veuillez réessayer plus tard.",
            innButton: 'Envoyer le numéro de TVA',
            loading: 'Chargement...',
            copied: 'Copié dans le presse-papiers',
        },
        recovery: {
            title: 'Récupération',
            description: "Pour récupérer l'accès au compte {email}, cliquez sur le bouton ci-dessous.",
            descriptionWithCompany:
                "Pour récupérer l'accès au compte {email} de la société {company_name}, cliquez sur le bouton ci-dessous.",
            button: 'Récupérer',
            successMessage: 'Nous vous avons envoyé un e-mail avec un lien de récupération.',
            successHint: "Si l'e-mail n'est pas arrivé, réessayez plus tard.",
            invalidEmail: 'E-mail incorrect',
        },
        profile: {
            title: 'Profil',
            companyData: "Données de l'entreprise",
            contactPerson: 'Personne de contact',
            mailingAddress: "Adresse d'expédition du courrier",
            settings: 'Paramètres',
            companyName: 'Nom',
            inn: 'Numéro de TVA',
            officeAddress: 'Adresse du bureau',
            name: 'Nom',
            email: 'E-mail',
            phone: 'Téléphone',
            interfaceLanguage: "Langue de l'interface",
            editData: 'Modifier les données',
            editDataMessage: "Pour modifier vos données ou celles de l'entreprise, contactez le support",
            logoutButton: 'Déconnexion',
            loading: 'Chargement des données du profil...',
            languageNames: {
                ru: 'Russe',
                en: 'Anglais',
                fr: 'Français',
                nl: 'Néerlandais',
                de: 'Allemand',
            },
        },
    },
    installPrompt: {
        title: "Installer l'application",
        description: "Installez l'application pour un accès plus rapide et facile aux services Office Hof",
        install: 'Installer',
        skip: 'Passer',
        androidTitle: 'Installation sur Android',
        androidSteps: [
            'Ouvrez le menu du navigateur (trois points en haut à droite)',
            "Sélectionnez « Ajouter à l'écran d'accueil » ou « Installer l'application »",
            "Confirmez l'installation",
        ],
        iosTitle: 'Installation sur iPhone',
        iosSteps: [
            'Appuyez sur le bouton Partager (carré avec flèche) dans Safari',
            "Faites défiler et sélectionnez « Sur l'écran d'accueil »",
            'Appuyez sur « Ajouter »',
        ],
        close: 'Fermer',
    },
};
